import React, { useEffect, useId, useRef, useState } from 'react';
import axios from 'axios';
import { Info } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { eventTabHref, STATUS_LABELS } from '../../lib/eventWorkspace';
import { inAppEventWhen, registrationCallouts } from '../../lib/formEventRegistration';
import { LABEL } from '../../lib/formConstants';
import { cn } from '../../lib/utils';
import RegistrationEventPicker from './RegistrationEventPicker';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const statusLabel = (status) => STATUS_LABELS[status] || (status === 'deleted' ? 'Deleted' : '');

/** "Published · 38 of 50 confirmed"; the counts are left off until the server has sent them. */
function statusLine(info) {
    const parts = [statusLabel(info.status)].filter(Boolean);
    if (Number.isFinite(info.confirmed)) {
        parts.push(Number.isFinite(info.capacity)
            ? `${info.confirmed} of ${info.capacity} confirmed`
            : `${info.confirmed} confirmed`);
    }
    return parts.join(' · ');
}

/**
 * The "Event registration" card in the workflow canvas's Form Settings panel (#919, E54). It is a form-wide setting, so
 * it sits with the other form-wide settings; QA round 2 moved it there from the page editor's Settings tab, which no
 * longer has it (owner decision, 2026-10-26).
 *
 * `value` is `settings.event_registration` (`{event_id}` or null). The toggle is a local "On": nothing is written until an
 * event is picked, so a save never carries `{event_id: undefined}`. A pick calls `onChange({event_id})` and switching off
 * calls `onChange(null)`; the canvas stores either one in the form's settings and marks it dirty. `dirty` says the form
 * holds a registration change that is not saved yet ("Save to apply"); the canvas keeps passing it while the panel
 * switches to Node Properties and back, which is how a remounted section knows `value` is not what the server has stored.
 *
 * The card under the picker comes from `GET /api/forms/{id}/event-registration` (E19): once on mount for the stored event
 * and again with `?event_id=` after an unsaved pick. Both are skipped for a new form, which has no id yet; the card then
 * shows the picked event's name, date and status with no counts until the first Save. A form that names no event reads
 * nothing on mount. The notices read one `state` and render inside a single always-mounted live region, so a screen
 * reader announces each change once.
 */
export default function EventRegistrationSettings({ formId, isNew = false, value, hasEmailField, onChange, dirty = false }) {
    const { getAuthHeader } = useAuth() || {};
    const authRef = useRef(getAuthHeader);
    authRef.current = getAuthHeader;
    const ids = useId();
    const headingId = `${ids}-heading`;
    const labelId = `${ids}-label`;
    const descId = `${ids}-desc`;
    const hintId = `${ids}-hint`;
    const pickerId = `${ids}-picker`;

    const selectedId = value?.event_id || null;
    const [enabled, setEnabled] = useState(Boolean(selectedId));
    const [localEvent, setLocalEvent] = useState(null);
    const [stored, setStored] = useState(null);
    const [storedFailed, setStoredFailed] = useState(false);
    const [pick, setPick] = useState(null);
    // The event the SAVED form names, as far as this section knows: the value it mounted with, unless the form was already
    // holding an unsaved change then (the builder unmounts this section when its tab changes, so a pick can outlive
    // it). A read for any selection other than this one is a read for an unsaved pick.
    const storedTarget = useRef(dirty ? null : selectedId);
    const picked = useRef(false);

    useEffect(() => { if (selectedId) setEnabled(true); }, [selectedId]);

    // E19, once: the stored event's summary, state and counts.
    useEffect(() => {
        if (isNew || !formId || !storedTarget.current) return undefined;
        let cancelled = false;
        axios.get(`${API}/forms/${formId}/event-registration`, { headers: authRef.current?.() || {} })
            .then((response) => {
                if (cancelled) return;
                setStored(response?.data && typeof response.data === 'object' ? response.data : null);
            })
            .catch(() => { if (!cancelled) setStoredFailed(true); });
        return () => { cancelled = true; };
    }, [formId, isNew]);

    // E19 again for an UNSAVED pick, so the card, the state and the counts are the picked event's, not the stored one's.
    useEffect(() => {
        setPick(null);
        if (isNew || !formId || !selectedId || selectedId === storedTarget.current) return undefined;
        let cancelled = false;
        axios.get(`${API}/forms/${formId}/event-registration`, {
            params: { event_id: selectedId },
            headers: authRef.current?.() || {},
        })
            .then((response) => { if (!cancelled) setPick({ id: selectedId, data: response?.data || null }); })
            .catch(() => { if (!cancelled) setPick({ id: selectedId, data: null }); });
        return () => { cancelled = true; };
    }, [selectedId, formId, isNew]);

    const toggle = () => {
        if (!enabled) {
            setEnabled(true);
            return;
        }
        setEnabled(false);
        if (selectedId || picked.current) {
            picked.current = false;
            onChange?.(null);
        }
    };

    const choose = (event) => {
        picked.current = true;
        setLocalEvent(event);
        onChange?.({ event_id: event.event_id });
    };

    // What the server says about the selected event, else what the picker knew when it was picked (a new form).
    let info = null;
    if (selectedId) {
        if (stored && stored.event_id === selectedId) info = stored;
        else if (pick && pick.id === selectedId && pick.data) info = pick.data;
    }
    const picker = localEvent && localEvent.event_id === selectedId ? localEvent : null;
    const eventSummary = info ? info.event : picker;
    const status = info ? info.status : (picker ? picker.status : null);
    // A draft pick on a form with no id yet has no server read, but its state is plain from its status.
    const state = info ? info.state : (picker && picker.status === 'draft' ? 'not_open' : null);
    const failed = !info && !picker && (storedFailed || (pick && !pick.data));

    const callouts = registrationCallouts({
        state,
        status,
        confirmed: info?.confirmed,
        capacity: info?.capacity,
        hasEmailField: enabled ? hasEmailField : undefined,
    });

    let cardTitle = 'Loading event…';
    if (eventSummary) cardTitle = eventSummary.name || 'Untitled event';
    else if (info) cardTitle = 'Event unavailable';
    else if (failed) cardTitle = 'Event details could not be loaded.';

    return (
        <section
            aria-labelledby={headingId}
            data-testid="event-registration-settings"
            className="space-y-3 rounded-lg border border-[#E8E5DF] bg-[#F7F6F2] p-3"
        >
            <div className="flex items-start justify-between gap-3">
                <h3 id={headingId} className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#6E6961]">Event registration</h3>
                {dirty && <span className="flex-shrink-0 text-xs font-medium text-amber-700">Save to apply</span>}
            </div>

            <div className="flex items-start gap-3">
                <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-labelledby={labelId}
                    aria-describedby={descId}
                    onClick={toggle}
                    className={cn(
                        'mt-0.5 inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900/30',
                        enabled ? 'bg-stone-900' : 'bg-stone-300',
                    )}
                >
                    <span
                        aria-hidden="true"
                        className={cn('inline-block h-4 w-4 rounded-full bg-white shadow transition-transform', enabled ? 'translate-x-4' : 'translate-x-0.5')}
                    />
                </button>
                <div className="min-w-0">
                    <p id={labelId} className="text-sm text-stone-800">Register respondents on an event</p>
                    <p id={descId} className="text-xs text-stone-500">Each respondent is added to the event&apos;s guest list as Confirmed.</p>
                </div>
            </div>

            {enabled && (
                <>
                    <div>
                        <label htmlFor={pickerId} className={LABEL}>Event</label>
                        <RegistrationEventPicker
                            id={pickerId}
                            value={selectedId}
                            onSelect={choose}
                            aria-describedby={selectedId ? undefined : hintId}
                        />
                        {!selectedId && (
                            <p id={hintId} className="mt-1.5 text-xs text-stone-500">Choose an event to turn registration on.</p>
                        )}
                    </div>

                    {selectedId && (
                        <div data-testid="event-registration-card" className="rounded-lg border border-stone-200 bg-white px-3 py-2.5">
                            <div className="flex items-start justify-between gap-2">
                                <p className="min-w-0 truncate text-sm font-semibold text-stone-900">{cardTitle}</p>
                                {eventSummary && (
                                    <a
                                        href={eventTabHref(selectedId)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="Opens in a new tab"
                                        className="flex-shrink-0 text-xs font-medium text-stone-700 underline"
                                    >
                                        Open event
                                    </a>
                                )}
                            </div>
                            {eventSummary && <p className="text-xs text-stone-600">{inAppEventWhen(eventSummary)}</p>}
                            {(info || picker) && <p className="text-xs text-stone-600">{statusLine({ ...(info || {}), status })}</p>}
                        </div>
                    )}

                    <p className="flex gap-2 text-[11px] leading-relaxed text-stone-500">
                        <Info aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                        <span>
                            {'People already on the guest list are moved to Confirmed. Repeat sign-ups don\'t create duplicates. Campaigns that start on "Registered for event" also start for these registrations.'}
                        </span>
                    </p>
                </>
            )}

            <div aria-live="polite" data-testid="event-registration-callouts" className="space-y-2">
                {callouts.map((callout) => (
                    <div
                        key={callout.code}
                        data-callout={callout.code}
                        className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                    >
                        {callout.text}
                    </div>
                ))}
            </div>
        </section>
    );
}
